import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';
import MainLayout from '../layout/MainLayout';
import DailyIntroGate from '../intro/DailyIntroGate';
import OccasionGate from '../occasion/OccasionGate';

// TEST MODE — these accounts get the first-run journey on every full page
// load, whether or not they have already answered it, and answering again
// simply writes another questionnaire row. Empty in normal operation: a new
// person sees it once and never again.
//
// A module-level latch, not state, so moving around inside the app does not
// replay it mid-session; a real reload does.
const FORCE_FIRST_RUN_EMAILS: string[] = [];
let firstRunForcedThisLoad = false;

export function shouldForceFirstRun(email?: string | null): boolean {
  if (firstRunForcedThisLoad) return false;
  if (!email) return false;
  return FORCE_FIRST_RUN_EMAILS.includes(email.toLowerCase());
}

/**
 * Spend the replay. Called when the journey is finished, not when its route
 * renders: signing in briefly mounts the questionnaire route before the login
 * page's own redirect lands, and marking it there burned the replay on a
 * frame the user never saw.
 */
export function markFirstRunReplayed(): void {
  firstRunForcedThisLoad = true;
}

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
}

function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading, needsQuestionnaire } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect ALL users to questionnaire if they haven't completed it
  // Skip if user still needs to change password first (password change takes priority)
  const isSuperSuperAdmin = user && user.role === 'super-super-admin';
  const replaying = shouldForceFirstRun(user?.email);
  if ((needsQuestionnaire || replaying) && location.pathname !== '/questionnaire'
      && !isSuperSuperAdmin && !user?.mustChangePassword) {
    return <Navigate to="/questionnaire" state={{ from: location }} replace />;
  }

  // Super-super-admin can ONLY access /admin/super and /profile — redirect everything else
  if (user?.role === 'super-super-admin' && !location.pathname.startsWith('/admin/super') && location.pathname !== '/profile' && location.pathname !== '/settings') {
    return <Navigate to="/admin/super" replace />;
  }

  // Check role-based access
  if (requiredRoles && requiredRoles.length > 0) {
    if (!user || !requiredRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Wrap protected routes with MainLayout (except questionnaire page)
  if (location.pathname === '/questionnaire') {
    return <>{children}</>;
  }

  // DailyIntroGate holds the whole app behind the once-a-day check-in
  // moment on the first login of the day (fails open on any API error).
  // A birthday plays before the check-in, and the check-in before the app.
  return (
    <OccasionGate>
      <DailyIntroGate>
        <MainLayout>
          {children}
        </MainLayout>
      </DailyIntroGate>
    </OccasionGate>
  );
}

export default ProtectedRoute;
